import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OrderModificationApprovalControllerService } from '@app/api/order-service/api/orderModificationApprovalController.service';
import type { OrderBaseDTO } from '@app/api/order-service/model/orderBaseDTO';
import type { OrderBaseEditResultVO } from '@app/api/order-service/model/orderBaseEditResultVO';
import type { OrderModificationRequestVO } from '@app/api/order-service/model/orderModificationRequestVO';
import type { ModApprovalTimeoutConfigDTO } from '@app/api/order-service/model/modApprovalTimeoutConfigDTO';
import type { ModApprovalTimeoutConfigVO } from '@app/api/order-service/model/modApprovalTimeoutConfigVO';
import type { ModApprovalThresholdConfigDTO } from '@app/api/order-service/model/modApprovalThresholdConfigDTO';
import type { ModApprovalThresholdConfigVO } from '@app/api/order-service/model/modApprovalThresholdConfigVO';
import type { ModificationDecisionDTO } from '@app/api/order-service/model/modificationDecisionDTO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';

const JSON_ACCEPT_OPTIONS = { httpHeaderAccept: 'application/json' } as const;

/** UI-facing aliases kept for existing feature consumers. */
export type OrderBaseEditResult = OrderBaseEditResultVO;
export type OrderModificationRequest = OrderModificationRequestVO;
export type ModApprovalTimeoutConfig = ModApprovalTimeoutConfigVO;
export type ModApprovalThresholdConfig = ModApprovalThresholdConfigVO;
export type ModificationDecision = ModificationDecisionDTO;

@Injectable({ providedIn: 'root' })
export class OrderModificationApprovalService {
  private readonly api = inject(OrderModificationApprovalControllerService);

  submitOrderBaseEdit(dto: OrderBaseDTO): Observable<OrderBaseEditResult> {
    return this.api
      .submitOrderBaseEdit(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: OrderBaseEditResultVO | Blob) =>
          this.handleBlobResponse<OrderBaseEditResultVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  getPending(orderId: number): Observable<OrderModificationRequest | null> {
    return this.api
      .getPending(orderId, 'response', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((res) => {
          if (res.status === 204 || !res.body) {
            return of(null);
          }
          return this.handleBlobResponse<OrderModificationRequestVO>(
            res.body as OrderModificationRequestVO | Blob,
          );
        }),
        catchError(() => of(null)),
      );
  }

  approve(
    requestId: number,
    level: number,
    decision?: ModificationDecision,
  ): Observable<OrderModificationRequest> {
    return this.api
      .approve(requestId, level, decision || {}, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: OrderModificationRequestVO | Blob) =>
          this.handleBlobResponse<OrderModificationRequestVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  reject(
    requestId: number,
    decision?: ModificationDecision,
  ): Observable<OrderModificationRequest> {
    return this.api
      .reject(requestId, decision || {}, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: OrderModificationRequestVO | Blob) =>
          this.handleBlobResponse<OrderModificationRequestVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  getTimeoutConfig(): Observable<ModApprovalTimeoutConfig> {
    return this.api
      .getTimeoutConfig('body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: ModApprovalTimeoutConfigVO | Blob) =>
          this.handleBlobResponse<ModApprovalTimeoutConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  upsertTimeoutConfig(
    dto: ModApprovalTimeoutConfigDTO,
  ): Observable<ModApprovalTimeoutConfig> {
    return this.api
      .upsertTimeoutConfig(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: ModApprovalTimeoutConfigVO | Blob) =>
          this.handleBlobResponse<ModApprovalTimeoutConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  getThresholdConfig(): Observable<ModApprovalThresholdConfig> {
    return this.api
      .getThresholdConfig('body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: ModApprovalThresholdConfigVO | Blob) =>
          this.handleBlobResponse<ModApprovalThresholdConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  upsertThresholdConfig(
    dto: ModApprovalThresholdConfigDTO,
  ): Observable<ModApprovalThresholdConfig> {
    return this.api
      .upsertThresholdConfig(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: ModApprovalThresholdConfigVO | Blob) =>
          this.handleBlobResponse<ModApprovalThresholdConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  private handleBlobResponse<T>(response: Blob | T): Observable<T> {
    if (response instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(response)) as Observable<T>;
    }
    return of(response as T);
  }

  private handleBlobError(error: unknown): Observable<never> {
    const err = error as { error?: unknown };
    if (err?.error instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(err.error)).pipe(
        catchError(() => throwError(() => error)),
        switchMap((parsedError: unknown) =>
          throwError(() => ({ ...(error as object), error: parsedError })),
        ),
      );
    }
    return throwError(() => error);
  }
}
